import React from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Eye, EyeOff, AlertTriangle, FileText } from "lucide-react";
import { BillActionDropdown } from "./BillActionDropdown";
import { BillImpactCell } from "./BillImpactCell";
import { PrintAIReviewButton } from "./PrintAIReviewButton";
import { Bill, BillAction } from "@/types/bill";

interface BillCardProps {
  bill: Bill;
  isSelected: boolean;
  sessionCode: string;
  onSelectBill: (billId: string) => void;
  onMonitorBill: (billId: string) => void;
  onIgnoreBill: (billId: string) => void;
  onProcessAction: (billId: string, action: BillAction) => void;
  onShowImpactDetails?: (bill: Bill) => void;
  handlePDFClick: (url: string, e: React.MouseEvent) => void;
  currentTab: string;
  processingActions?: Record<string, BillAction>;
}

export function BillCard({
  bill,
  isSelected,
  onSelectBill,
  onMonitorBill,
  onIgnoreBill,
  onProcessAction,
  onShowImpactDetails,
  handlePDFClick,
  currentTab,
  processingActions,
}: BillCardProps) {
  const formatDate = (dateString?: string) => {
    return dateString ? new Date(dateString).toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) : "";
  };

  return (
    <Card className={`overflow-hidden ${isSelected ? 'ring-2 ring-primary' : ''}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <span className="font-medium">{bill.billNumber}</span>
            {!bill.fullTextAvailable && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  </TooltipTrigger>
                  <TooltipContent>
                    Full text not available
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          <div className="flex gap-1">
            {bill.url && bill.fullTextAvailable && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => handlePDFClick(bill.url, e)}
                      className="px-2 w-8 h-8"
                    >
                      <FileText className="h-4 w-4" />
                      <span className="sr-only">View Bill PDF</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    View Bill PDF
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-muted-foreground">Title</p>
          <p className="text-sm line-clamp-2">{bill.title || 'No Title'}</p>
        </div>

        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Impact</p>
            <BillImpactCell
              analysis={bill.monitor_analysis}
              onClick={() => onShowImpactDetails?.(bill)}
            />
          </div>

          {bill.crossfileBillNumber && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">Crossfile</p>
              <Badge variant="outline">{bill.crossfileBillNumber}</Badge>
            </div>
          )}
        </div>

        <div>
          <p className="text-sm font-medium text-muted-foreground">Modified</p>
          <p className="text-sm">{formatDate(bill.lastModified)}</p>
        </div>

        {bill.versions && bill.versions.length > 0 && (
          <div className="flex gap-2">
            <PrintAIReviewButton
              latestVersion={bill.versions[bill.versions.length - 1]}
              bill={bill}
              loadingAction={processingActions?.[bill.billNumber]}
            />
            {/*<PromptResultsDropdown*/}
            {/*  latestVersion={bill.versions[bill.versions.length - 1]}*/}
            {/*  loadingAction={processingActions?.[bill.billNumber]}*/}
            {/*/>*/}
          </div>
        )}
      </CardContent>

      <CardFooter className="px-4 py-3 bg-muted/20 flex justify-between">
        <Button
          size="sm"
          variant={isSelected ? "default" : "outline"}
          onClick={() => onSelectBill(bill.billNumber)}
          className="px-3"
        >
          {isSelected ? "Selected" : "Select"}
        </Button>

        <div className="flex gap-2">
          {currentTab === "New" && (
            <>
              <TooltipProvider>
               <Tooltip>
              <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onMonitorBill(bill.billNumber)}
                className="px-2 w-8 h-8"
              >
                <Eye className="h-4 w-4" />
                <span className="sr-only">Monitor Bill</span>
              </Button>
                </TooltipTrigger>
              <TooltipContent side="top">
                Monitor
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
              <TooltipProvider>
  <Tooltip>
    <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onIgnoreBill(bill.billNumber)}
                className="px-2 w-8 h-8"
              >
                <EyeOff className="h-4 w-4" />
                <span className="sr-only">Ignore Bill</span>
              </Button>
      </TooltipTrigger>
    <TooltipContent side="top">
      Ignore
    </TooltipContent>
  </Tooltip>
</TooltipProvider>
            </>
          )}
          {currentTab === "Monitoring" && (
            <>
              <BillActionDropdown
                onAction={(action: BillAction) => onProcessAction(bill.billNumber, action)}
                disabled={!bill.fullTextAvailable || !!processingActions?.[bill.billNumber]}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => onIgnoreBill(bill.billNumber)}
                className="px-2 w-8 h-8"
              >
                <EyeOff className="h-4 w-4" />
                <span className="sr-only">Stop Monitoring</span>
              </Button>
            </>
          )}
          {currentTab === "Ignored" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onMonitorBill(bill.billNumber)}
              className="px-2 w-8 h-8"
            >
              <Eye className="h-4 w-4" />
              <span className="sr-only">Resume Monitoring</span>
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
}